// HAND-ROLLED — documented exception per
// feedback_deterministic_codegen_over_hand_rolling.md. The activity-feed
// surface has no entity-hint (it's a denormalized projection across many
// entities, not a single entity's CRUD surface), so the v2gen.py path
// doesn't apply. When an `activity-feed.entity-hint.yaml` is authored in
// a future CHG, this file becomes generator-emitted and the hand-roll is
// removed.
//
// CHG-1643 / Wave 1 Tier 1 — port of v1 ActivityFeed.tsx + spec
// specs/tier-05-experience/activity-feed.cross-screen.yaml. Lives next
// to its generator-emitted siblings so the page file stays thin and the
// import surface mirrors the rest of v2.

import type { ListPageConfig } from "../components/composition/ListPage";
import { activityFeedApi, type ActivityEntry } from "../api/activity-feed";
import type { ReactNode } from "react";

const CATEGORY_LABEL: Record<string, string> = {
  donation: "Donation",
  user: "User",
  communication: "Communication",
  campaign: "Campaign",
  designation: "Designation",
  pledge: "Pledge",
  recurring: "Recurring",
  system: "System",
};

function categoryColumn(row: ActivityEntry): ReactNode {
  return CATEGORY_LABEL[row.category] ?? row.category;
}

function actorColumn(row: ActivityEntry): ReactNode {
  return row.actor_name ?? row.actor_email ?? "—";
}

function entityColumn(row: ActivityEntry): ReactNode {
  if (row.entity_name) return row.entity_name;
  if (row.entity_type && row.entity_id) {
    return `${row.entity_type} ${row.entity_id.slice(0, 8)}…`;
  }
  return "—";
}

export function buildActivityFeedListConfig(
  scope: "org" | "platform" = "org",
): ListPageConfig<ActivityEntry> {
  const isPlatform = scope === "platform";
  return {
    title: isPlatform ? "Platform Activity" : "Activity Feed",
    subtitle: isPlatform
      ? "Real-time chronological view of activity across every organization."
      : "Real-time chronological view of everything happening in your organization.",
    queryKey: ["activity-feed", scope] as const,
    queryFn: ({ page, pageSize, search }) =>
      activityFeedApi.listActivity({ page, pageSize, search, scope }),
    defaultSort: { field: "timestamp", direction: "desc" },
    pageSize: 50,
    search: { enabled: true, placeholder: "Search activity by title, description, actor…" },
    emptyState: {
      title: "No activity yet",
      subtitle: isPlatform
        ? "Activity will appear here as tenants on the platform record actions."
        : "Activity will appear here as your team and donors take action.",
    },
    columns: [
      { field: "timestamp", label: "When", type: "datetime", sortable: true },
      { field: "category", label: "Kind", type: "custom", render: categoryColumn },
      { field: "title", label: "Title", type: "text" },
      { field: "description", label: "Detail", type: "text" },
      { field: "actor_email", label: "Actor", type: "custom", render: actorColumn },
      { field: "entity_name", label: "Entity", type: "custom", render: entityColumn },
      ...(isPlatform
        ? [{ field: "org_name" as const, label: "Org", type: "text" as const }]
        : []),
    ],
  };
}

export const activityFeedListConfig = buildActivityFeedListConfig("org");
export const platformActivityFeedListConfig = buildActivityFeedListConfig("platform");
